/*
 * File-based article/blog system. Each article is a Markdown file in
 * src/content/articles/<slug>.md with a small YAML-ish frontmatter block:
 *
 *   ---
 *   title: How to ...
 *   description: One-line meta description for search + social.
 *   date: 2026-09-11          # display/sort date
 *   publishAt: 2026-09-18     # goes live on/after this date (defaults to `date`)
 *   author: CalculateMyPeptides
 *   tags: GLP-1, dosing
 *   cover: /og/default.png    # optional social image path
 *   draft: false              # true = never shown, even if the date has passed
 *   ---
 *   Markdown body...
 *
 * Scheduling: an article is only "live" when it isn't a draft AND its publishAt
 * date has arrived. Because the site is prerendered, a future-dated article stays
 * out of the build (and the sitemap) until a build runs on/after that date — the
 * daily rebuild cron on the VPS is what flips scheduled drafts live. See
 * deploy/publish-due.sh and ARTICLES.md.
 */
import { marked } from 'marked'

marked.setOptions({ gfm: true, breaks: false })

// Eagerly load every article's raw text at build/bundle time.
const files = import.meta.glob('../content/articles/*.md', { query: '?raw', import: 'default', eager: true })

// today's date as YYYY-MM-DD (lexicographically comparable to frontmatter dates)
const TODAY = new Date().toISOString().slice(0, 10)

// Minimal frontmatter parser — our frontmatter is authored by us, so it only needs
// to handle `key: value`, quoted strings, and simple comma lists (tags).
function parseFrontmatter(raw) {
  const m = /^---\s*\n([\s\S]*?)\n---\s*\n?([\s\S]*)$/.exec(raw)
  if (!m) return { data: {}, body: raw }
  const data = {}
  for (const line of m[1].split('\n')) {
    const mm = /^([A-Za-z0-9_]+):\s*(.*)$/.exec(line.trim())
    if (!mm) continue
    let v = mm[2].trim().replace(/^["']|["']$/g, '')
    data[mm[1]] = v
  }
  return { data, body: m[2] }
}

function toArticle(path, raw) {
  const { data, body } = parseFrontmatter(raw)
  const slug = data.slug || path.split('/').pop().replace(/\.md$/, '')
  const date = data.date || '1970-01-01'
  const publishAt = data.publishAt || date
  const words = body.trim().split(/\s+/).length
  return {
    slug,
    title: data.title || slug,
    description: data.description || '',
    date,
    publishAt,
    author: data.author || 'CalculateMyPeptides',
    tags: data.tags ? data.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
    cover: data.cover || '/og/default.png',
    draft: String(data.draft).toLowerCase() === 'true',
    readingMins: Math.max(1, Math.round(words / 200)),
    body,
    html: marked.parse(body),
    live: String(data.draft).toLowerCase() !== 'true' && publishAt <= TODAY,
    status: String(data.draft).toLowerCase() === 'true' ? 'draft' : publishAt <= TODAY ? 'live' : 'scheduled',
  }
}

const ALL = Object.entries(files)
  .map(([path, raw]) => toArticle(path, raw))
  .sort((a, b) => (a.date < b.date ? 1 : -1)) // newest first

// Public API: only ever expose LIVE articles (scheduled/draft ones stay hidden).
export const ARTICLES = ALL.filter((a) => a.live)
// Every article incl. scheduled + drafts — for the admin pipeline view ONLY.
// Sorted with upcoming/scheduled first so the pipeline reads top-down.
export const ALL_ARTICLES = [...ALL].sort((a, b) => (a.publishAt < b.publishAt ? 1 : -1))
export const getArticle = (slug) => ARTICLES.find((a) => a.slug === slug) || null

// Human date, e.g. "September 11, 2026"
export function formatDate(d) {
  const dt = new Date(d + 'T00:00:00')
  return Number.isNaN(dt.getTime()) ? d : dt.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
}
